import { useEffect, useMemo, useState } from "react";
import type { Etablissement } from "@prisma/client";
import Input from "@app/components/Input";
import SelectCustom from "@app/components/SelectCustom";
import SelectEtablissement from "@app/components/SelectEtablissement";
import { QRCodeSVG } from "qrcode.react";
import { UserMetadataResponse } from "@api/src/types/responses";
import { UserWithMyRooms } from "@api/src/types/user";

export default function QRCode() {
  const [userStatuses, setUserStatuses] = useState<UserMetadataResponse["data"]["userStatuses"]>([]);
  const [userSpecialites, setUserSpecialites] = useState<UserMetadataResponse["data"]["userSpecialites"]>([]);
  const [etablissements, setEtablissements] = useState<UserMetadataResponse["data"]["etablissements"]>([]);
  const [showQRCode, setShowQRCode] = useState(false);
  const [defaultValues, setValues] = useState<{
    status_pro: string;
    specialite: string;
    etablissement: Etablissement["name"];
    hopital: string;
    service: string;
  }>({
    status_pro: "",
    specialite: "",
    etablissement: "",
    hopital: "",
    service: "",
  });

  const statusOptions = userStatuses.map(({ status }) => ({ value: status, label: status }));

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL}/user-metadata`, {
      credentials: "include",
      method: "GET",
    })
      .then((response) => response.json() as Promise<UserMetadataResponse>)
      .then((response) => {
        if (response.error) {
          alert(response.error);
          return;
        } else if (response.ok) {
          setUserStatuses(response.data.userStatuses);
          setUserSpecialites(response.data.userSpecialites);
          setEtablissements(response.data.etablissements);
          const _statusOptions = response.data.userStatuses.map(({ status }) => ({ value: status, label: status }));
          setValues({
            status_pro: _statusOptions[0].label,
            specialite: response.data.userSpecialites[0].specialite,
            etablissement: response.data.etablissements[0].name,
            hopital: "",
            service: _statusOptions[0].label,
          });
        }
      });
  }, []);

  useEffect(() => {
    const defaultValues = window.localStorage.getItem("qr_code_default_values");
    if (defaultValues) {
      setValues(JSON.parse(defaultValues));
    }
  }, []);

  const url = useMemo(() => {
    const _url = new URL(window.location.origin);
    _url.pathname = "/from_qrcode";
    const searchParams = new URLSearchParams();
    searchParams.append("status_pro", defaultValues.status_pro);
    searchParams.append("specialite", defaultValues.specialite);
    searchParams.append("etablissement", defaultValues.etablissement);
    searchParams.append("service", defaultValues.service);
    searchParams.append("hopital", defaultValues.hopital);

    _url.search = searchParams.toString();
    return _url.toString();
  }, [defaultValues]);

  if (showQRCode) {
    return (
      <main
        className="flex h-full w-full flex-col items-center overflow-auto bg-[#EAEEF4]"
        key={JSON.stringify(defaultValues)}
        onClick={() => setShowQRCode(false)}
      >
        <section className="flex h-full w-full flex-col overflow-hidden px-4">
          <div className="mb-4 flex flex-col">
            <h1 className="py-2 text-2xl font-bold text-sky-800">MedSpot.fr 🩺</h1>
            <span>
              {defaultValues?.status_pro} - {defaultValues?.specialite} - {defaultValues?.service} -
              {defaultValues?.etablissement}
            </span>
          </div>
          <div className="flex basis-full flex-col items-center justify-center overflow-hidden p-10 text-sky-800 [&_image]:overflow-hidden [&_image]:rounded-full">
            <QRCodeSVG
              className="h-full w-full"
              value={url}
              bgColor="#EAEEF4"
              fgColor="#065985"
              // imageSettings={{
              //   src: "/icon_dark_512.png",
              //   height: 50,
              //   width: 50,
              //   excavate: true,
              // }}
            />
          </div>
        </section>
        <span className="block w-full max-w-full overflow-hidden break-all p-2 text-xs text-sky-800 opacity-40">
          {url}
        </span>
      </main>
    );
  }

  return (
    <main className="flex h-full w-full flex-col items-center overflow-auto" key={JSON.stringify(defaultValues)}>
      <section className="flex h-full w-full flex-col px-4 md:max-w-6xl">
        <div className="mt-4 flex w-full justify-between md:mt-10">
          <h1 className="py-2 text-2xl font-bold text-sky-800">MedSpot.fr 🩺</h1>
        </div>
        <span className="my-5 text-2xl text-orange-400 md:max-w-[70%]">Génération d'un QRCode</span>

        <form
          method="POST"
          className={`flex w-full max-w-md flex-shrink flex-grow flex-col py-4 !text-left`}
          onBlur={(e: React.FocusEvent<HTMLFormElement>) => {
            const values = new FormData(e.currentTarget);
            console.log(Object.fromEntries(values));
            setValues(Object.fromEntries(values) as typeof defaultValues);
            window.localStorage.setItem("qr_code_default_values", JSON.stringify(Object.fromEntries(values)));
          }}
        >
          <SelectEtablissement etablissements={etablissements} defaultValues={defaultValues as UserWithMyRooms} />

          <Input
            label="Service"
            name="service"
            defaultValue={defaultValues?.service}
            type="text"
            required
            placeholder="Anesth-ortho, Soin intensif cardio, Chir-ortho-A, etc."
          />
          <Input
            label="Spécialité"
            name="specialite"
            defaultValue={defaultValues?.specialite}
            type="text"
            required
            placeholder="Anesthésie, Cardiologie, Chirurgie, Gynécologie, Neurologie, Pédiatrie, Psychiatrie, Radiologie, Urgences, etc."
          >
            <SelectCustom
              name="specialite"
              defaultValue={{ value: defaultValues?.specialite, label: defaultValues?.specialite }}
              options={userSpecialites.map(({ specialite }) => ({ value: specialite, label: specialite }))}
              isMulti={false}
              instanceId="specialite"
              classNamePrefix="specialite"
            />
          </Input>
          <Input
            label="Statut"
            name="status_pro"
            defaultValue={defaultValues?.status_pro}
            type="text"
            required
            placeholder="PUPH, PH, CCA, AHU, Interne..."
          >
            <SelectCustom
              name="status_pro"
              defaultValue={{ value: defaultValues?.status_pro, label: defaultValues?.status_pro }}
              options={statusOptions}
              isMulti={false}
              instanceId="status_pro"
              classNamePrefix="status_pro"
            />
          </Input>

          <button
            className="mr-auto inline-block rounded-lg border-none bg-sky-800 px-10 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
            type="button"
            onClick={() => setShowQRCode(true)}
          >
            Générer le QRCode
          </button>
        </form>
      </section>
    </main>
  );
}
